import React, { useState, useCallback, useEffect } from "react";
import {
  EuiSelectable,
  EuiSelectableOption,
  EuiText,
  EuiCheckbox,
  EuiSpacer,
  EuiFlexGroup,
  EuiFlexItem,
  EuiButton,
  EuiFieldText,
} from "@elastic/eui";
import MonitoringProfilesService from "services/AdminConsole/Monitoring/Profiles/MonitoringProfilesService";
import { AlertType } from "../../profileTypes";
import { useProfileStyles } from "../../profileStyles";
import Popover from "components/Popover/Popover";
import Button from "components/Buttons/Button";
import BadgeCount from "components/Badge/BadgeCount";
import { ALL_OBJECTS_LABEL } from "../../profileConsts";

interface AlertObjectSelectorProps {
  alert: AlertType;
  clientId: number;
  monitoringProfileId: number;
  onSelectionChange: (
    selectedObjectNames: string[],
    alertId: number,
    subtypeId: number | null,
    alertLevel: number
  ) => void; // Updated signature
  objectTypeName?: string;
  objectOptions?: any;
}

type SelectableObjectOption = EuiSelectableOption & {
  label: string;
};

const sortOptionsWithCheckedFirst = (
  optionsToSort: SelectableObjectOption[]
): SelectableObjectOption[] => {
  return [...optionsToSort].sort((a, b) => {
    const aIsChecked = a.checked === "on";
    const bIsChecked = b.checked === "on";

    if (aIsChecked && !bIsChecked) return -1;
    if (!aIsChecked && bIsChecked) return 1;

    return a.label.localeCompare(b.label);
  });
};

const AlertObjectSelector: React.FC<AlertObjectSelectorProps> = ({
  alert,
  clientId,
  monitoringProfileId,
  onSelectionChange,
  objectTypeName,
  objectOptions,
}) => {
  const sharedClasses = useProfileStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<SelectableObjectOption[]>([]);
  const [tempOptions, setTempOptions] = useState<SelectableObjectOption[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showAddObjectInput, setShowAddObjectInput] = useState(false);
  const [newObjectInputValue, setNewObjectInputValue] = useState("");
  const [calculatedPanelWidth, setCalculatedPanelWidth] = useState<string>("400px");

  useEffect(() => {
    const currentSelectedNames = new Set(alert.selectedObjectNames || []);
    const finalOptionsMap = new Map<string, SelectableObjectOption>();

    const alertAssignedObjects = alert.objectAssignments || [];
    for (const assignedObj of alertAssignedObjects) {
      if (assignedObj && assignedObj.name) {
        finalOptionsMap.set(assignedObj.name, {
          label: assignedObj.name,
          checked: currentSelectedNames.has(assignedObj.name) ? "on" : undefined,
          key: assignedObj.name,
        });
      }
    }

    for (const name of Array.from(currentSelectedNames)) {
      if (!finalOptionsMap.has(name)) {
        finalOptionsMap.set(name, {
          label: name,
          checked: "on",
          key: name,
        });
      }
    }

    const initialCombinedOptions = Array.from(finalOptionsMap.values());
    const sortedInitialOptions = sortOptionsWithCheckedFirst(initialCombinedOptions);

    setOptions(sortedInitialOptions);
    handleSetTempOptions(sortedInitialOptions);
  }, []);

  const handleSetTempOptions = (newTempOptions: SelectableObjectOption[]) => {
    const filteredOptions = newTempOptions.filter((option) => option.label !== ALL_OBJECTS_LABEL);

    const anyItemChecked = filteredOptions.some((option) => option.checked === "on");

    const defaultOption: SelectableObjectOption = {
      label: ALL_OBJECTS_LABEL,
      checked: anyItemChecked ? undefined : "on",
      key: ALL_OBJECTS_LABEL,
    };

    setTempOptions([defaultOption, ...filteredOptions]);
  };

  const addNewObjectToTempOptions = (objectName: string): boolean => {
    if (objectName.trim() === "") return false;

    const newLabel = objectName.trim();
    const alreadyExists = tempOptions.some((opt) => opt.label === newLabel);

    if (!alreadyExists) {
      const newOption: SelectableObjectOption = {
        label: newLabel,
        checked: "on",
        key: newLabel,
      };
      const updatedOptions = [...tempOptions, newOption];
      handleSetTempOptions(sortOptionsWithCheckedFirst(updatedOptions));
      return true;
    }
    return false;
  };

  const fetchOptions = useCallback(async () => {
    if (isLoading || !monitoringProfileId || !alert.id || !objectTypeName || objectOptions) {
      return;
    }
    setIsLoading(true);

    try {
      const payload = {
        alertAssignment: {
          id: alert.id,
          subtypeId: alert.subtypeId,
        },
        monitoringProfileAssignments: [{ id: monitoringProfileId }],
      };
      const result = await MonitoringProfilesService.getAlertObjects({ clientId, payload });
      const allKnownSelectedNames = new Set(alert.selectedObjectNames || []);
      const finalOptionsMap = new Map<string, SelectableObjectOption>();

      const apiRawObjects = result?.availableObjects || [];
      for (const obj of apiRawObjects) {
        finalOptionsMap.set(obj.name, {
          label: obj.name,
          checked: allKnownSelectedNames.has(obj.name) ? "on" : undefined,
          key: obj.name,
        });
      }

      for (const name of Array.from(allKnownSelectedNames)) {
        if (!finalOptionsMap.has(name)) {
          finalOptionsMap.set(name, {
            label: name,
            checked: "on",
            key: name,
          });
        }
      }

      const alertAssignedObjects = alert.objectAssignments || [];
      for (const assignedObj of alertAssignedObjects) {
        if (assignedObj && assignedObj.name) {
          const isSelected = allKnownSelectedNames.has(assignedObj.name);
          finalOptionsMap.set(assignedObj.name, {
            label: assignedObj.name,
            checked: isSelected ? "on" : undefined,
            key: assignedObj.name,
          });
        }
      }

      const newCombinedOptions = Array.from(finalOptionsMap.values());
      const sortedOptions = sortOptionsWithCheckedFirst(newCombinedOptions);

      setOptions(sortedOptions);
      handleSetTempOptions(sortedOptions);
      setHasFetched(true);
    } catch (error) {
      console.error("*** Failed to fetch alert objects:", error);
      setHasFetched(false);
    } finally {
      setIsLoading(false);
    }
  }, [
    alert.id,
    alert.subtypeId,
    alert.selectedObjectNames,
    clientId,
    monitoringProfileId,
    objectTypeName,
    isLoading,
    objectOptions,
  ]);

  useEffect(() => {
    if (!objectOptions) return;
    const allKnownSelectedNames = new Set(alert.selectedObjectNames || []);
    const finalOptionsMap = new Map<string, SelectableObjectOption>();

    const apiRawObjects = objectOptions || [];
    for (const obj of apiRawObjects) {
      finalOptionsMap.set(obj.name, {
        label: obj.name,
        checked: allKnownSelectedNames.has(obj.name) ? "on" : undefined,
        key: obj.name,
      });
    }

    for (const name of Array.from(allKnownSelectedNames)) {
      if (!finalOptionsMap.has(name)) {
        finalOptionsMap.set(name, {
          label: name,
          checked: "on",
          key: name,
        });
      }
    }

    const alertAssignedObjects = alert.objectAssignments || [];
    for (const assignedObj of alertAssignedObjects) {
      if (assignedObj && assignedObj.name) {
        const isSelected = allKnownSelectedNames.has(assignedObj.name);
        finalOptionsMap.set(assignedObj.name, {
          label: assignedObj.name,
          checked: isSelected ? "on" : undefined,
          key: assignedObj.name,
        });
      }
    }

    const newCombinedOptions = Array.from(finalOptionsMap.values());
    const sortedOptions = sortOptionsWithCheckedFirst(newCombinedOptions);

    setOptions(sortedOptions);
    handleSetTempOptions(sortedOptions);
  }, [clientId, objectOptions]);

  const handlePopoverOpen = () => {
    handleSetTempOptions([...options]);
    setIsPopoverOpen(true);
    if (!hasFetched && objectTypeName) {
      fetchOptions();
    }
    setShowAddObjectInput(false);
    setNewObjectInputValue("");
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
    setShowAddObjectInput(false);
    setNewObjectInputValue("");
  };

  const handleSelectableChange = (processedOptionsFromSelectable: SelectableObjectOption[]) => {
    const allObjectsLabel = ALL_OBJECTS_LABEL;
    const optionsToSet = [...processedOptionsFromSelectable];

    const newAllOption = optionsToSet.find((opt) => opt.label === allObjectsLabel);
    const oldAllOption = tempOptions.find((opt) => opt.label === allObjectsLabel);

    const newAllIsChecked = newAllOption?.checked === "on";
    const oldAllIsChecked = oldAllOption?.checked === "on";

    if (newAllIsChecked && !oldAllIsChecked) {
      optionsToSet.forEach((opt) => {
        opt.checked = opt.label === allObjectsLabel ? "on" : undefined;
      });
    } else if (!newAllIsChecked && oldAllIsChecked) {
      const anyRegularOptionChanged = optionsToSet
        .filter((opt) => opt.label !== allObjectsLabel)
        .some((newOpt) => {
          const oldOpt = tempOptions.find((o) => o.key === newOpt.key);
          return newOpt.checked !== oldOpt?.checked;
        });

      if (!anyRegularOptionChanged && newAllOption) {
        newAllOption.checked = "on";
      }
    }

    handleSetTempOptions(optionsToSet);
  };

  const handleApply = () => {
    let currentTempOptions = [...tempOptions];

    if (showAddObjectInput && newObjectInputValue.trim() !== "") {
      const newLabel = newObjectInputValue.trim();
      const alreadyExists = currentTempOptions.some((opt) => opt.label === newLabel);
      if (!alreadyExists) {
        const newOption: SelectableObjectOption = {
          label: newLabel,
          checked: "on",
          key: newLabel,
        };
        currentTempOptions.push(newOption);
      }
    }

    const sortedFinalOptions = sortOptionsWithCheckedFirst(currentTempOptions);
    setOptions(sortedFinalOptions);

    const selectedNames = sortedFinalOptions
      .filter((option) => option.checked === "on")
      .map((option) => option.label);

    // Pass the alert ID, subtypeId, and alertLevel to identify which specific alert is being updated
    onSelectionChange(selectedNames, alert.id, alert.subtypeId ?? null, alert.alertLevel || 1);
    handlePopoverClose();
  };

  const handleCancel = () => {
    handlePopoverClose();
  };

  const handleClickAddObject = () => {
    setShowAddObjectInput(true);
  };

  const handleNewObjectInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewObjectInputValue(e.target.value);
  };

  const handleNewObjectKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (newObjectInputValue.trim() !== "") {
        if (addNewObjectToTempOptions(newObjectInputValue)) {
          setNewObjectInputValue("");
          setShowAddObjectInput(false);
        }
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setShowAddObjectInput(false);
      setNewObjectInputValue("");
    }
  };

  const selectedCount = options.filter(
    (opt) => opt.checked === "on" && opt.label !== ALL_OBJECTS_LABEL
  ).length;
  const buttonText = `${objectTypeName || "Object"}`;

  const isDisabled = !objectTypeName;

  const filterButton = (
    <EuiButton
      className={sharedClasses.selectButton}
      style={{
        background: "var(--card-background3)",
        border: "1px solid var(--border-color)",
        color: "var(--text)",
        textDecoration: "none",
        borderRadius: "10px",
      }}
      iconType="arrowDown"
      iconSide="right"
      onClick={handlePopoverOpen}
      isLoading={isLoading && !isPopoverOpen}
      isDisabled={isDisabled}
      title={buttonText}>
      <span
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}>
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}>
          {buttonText}
        </span>
        {selectedCount > 0 && (
          <span style={{ flexShrink: 0, marginLeft: "8px" }}>
            <BadgeCount number={selectedCount} />
          </span>
        )}
      </span>
    </EuiButton>
  );

  const calculatePanelWidth = useCallback(() => {
    if (!tempOptions || tempOptions.length === 0) {
      return "400px";
    }

    let maxLength = 0;
    tempOptions.forEach((option) => {
      if (option.label && option.label.length > maxLength) {
        maxLength = option.label.length;
      }
    });

    const averageCharWidth = 7.7;
    const dynamicWidth = maxLength * averageCharWidth;

    return `${Math.min(900, Math.max(400, dynamicWidth))}px`;
  }, [tempOptions]);

  useEffect(() => {
    if (isPopoverOpen && tempOptions.length > 0) {
      setCalculatedPanelWidth(calculatePanelWidth());
    }
  }, [tempOptions, isPopoverOpen, calculatePanelWidth]);

  if (!objectTypeName) {
    return <EuiText size="s">Not Applicable</EuiText>;
  }

  return (
    <div className={sharedClasses.alertObjectSelectorButton}>
      <Popover
        ownFocus={true}
        isOpen={isPopoverOpen}
        onClose={handlePopoverClose}
        anchorPosition="downLeft"
        panelPaddingSize="none"
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Escape") {
            if (showAddObjectInput) return;
            e.stopPropagation();
            handlePopoverClose();
          }
        }}
        button={filterButton}
        panelStyle={{
          minWidth: calculatedPanelWidth,
          borderRadius: "6px",
          overflow: "hidden",
        }}
        children={
          <>
            <div
              className={`${sharedClasses.hideEuiCheckmarks} ${sharedClasses.overrideTruncation}`}>
              <EuiSelectable
                singleSelection={false}
                isLoading={isLoading}
                listProps={{ showIcons: false }}
                searchable
                searchProps={{
                  placeholder: `Search ${objectTypeName}s...`,
                  compressed: false,
                  css: {
                    borderTopLeftRadius: 10,
                    borderTopRightRadius: 10,
                    borderBottom: ".6px solid var(--border-color)",
                    boxShadow: "none",
                    backgroundColor: "var(--card-background)",
                  },
                }}
                options={tempOptions}
                onChange={handleSelectableChange}
                renderOption={(option) => {
                  const isChecked = option.checked === "on";
                  const isDefaultOption = option.label === ALL_OBJECTS_LABEL;

                  return (
                    <div className={sharedClasses.optionRow} style={{ position: "relative" }}>
                      <EuiCheckbox
                        id={`checkbox_${objectTypeName}_${option.key || option.label}`}
                        checked={isChecked}
                        onChange={() => {}}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        label={
                          <div
                            className={sharedClasses.checkboxLabel}
                            style={{
                              position: "absolute",
                              left: 0,
                              paddingLeft: "32px",
                            }}>
                            <span style={isDefaultOption ? { fontWeight: 600 } : undefined}>
                              {option.label}
                            </span>
                          </div>
                        }
                      />
                    </div>
                  );
                }}>
                {(list, search) => (
                  <div style={{ width: "100%" }}>
                    {search}
                    {showAddObjectInput && (
                      <div style={{ marginBottom: "16px" }}>
                        <EuiFieldText
                          placeholder="Enter new object name and press Enter"
                          value={newObjectInputValue}
                          onChange={handleNewObjectInputChange}
                          onKeyDown={handleNewObjectKeyDown}
                          aria-label="Add new object name"
                          autoFocus
                          style={{ margin: "8px 16px", width: "calc(100% - 32px)" }}
                        />
                      </div>
                    )}
                    {list}
                  </div>
                )}
              </EuiSelectable>
            </div>
            <EuiSpacer size="s" />
            <EuiFlexGroup
              gutterSize="m"
              justifyContent="flexEnd"
              style={{ padding: "8px", backgroundColor: "var(--app-background)" }}>
              <EuiFlexItem grow={false}>
                <Button
                  type="empty"
                  onClick={handleClickAddObject}
                  text="Add Object"
                  disabled={showAddObjectInput}
                />
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <Button type="secondary" onClick={handleCancel} text="Cancel" />
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiButton fill minWidth={false} onClick={handleApply} size="s">
                  Apply
                </EuiButton>
              </EuiFlexItem>
            </EuiFlexGroup>
          </>
        }
      />
    </div>
  );
};

export default AlertObjectSelector;
